// src/components/Pricing.jsx
import React, { Component } from 'react';
import axios from 'axios';

class Pricing extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      products: [],
      error: null,
      busyAction: null,
      hasActiveSubscription: false,
      creditsBalance: 0
    };

    this.API_BASE = process.env.REACT_APP_API_ENDPOINT;
  }

  async componentDidMount() {
    await this.fetchProducts();
    await this.fetchSubscriptionStatus();
    await this.loadCredits();
  }

  /* ----------------------------
     FETCH PRODUCTS
  ----------------------------- */
  async fetchProducts() {
    try {
      const res = await axios.get(`${this.API_BASE}/stripe/products`, {
        withCredentials: true
      });

      this.setState({ products: res.data.products || [], loading: false });
    } catch (err) {
      console.error('Error loading products', err);
      this.setState({
        error: 'Unable to load pricing from server',
        loading: false
      });
    }
  }

  /* ----------------------------
     SUBSCRIPTION STATUS
  ----------------------------- */
  async fetchSubscriptionStatus() {
    try {
      const res = await axios.get(
        `${this.API_BASE}/stripe/subscriptions/history`,
        { withCredentials: true }
      );

      const subs = res.data.subscriptions || [];

      const active = subs.some(
        s => s.status === 'active' || s.status === 'trialing'
      );

      this.setState({ hasActiveSubscription: active });
    } catch (err) {
      console.error("Error loading subscription status:", err);
    }
  }

  /* ----------------------------
     CREDIT BALANCE
  ----------------------------- */
  loadCredits = async () => {
    try {
      const res = await axios.get(
        `${this.API_BASE}/stripe/credits/balance`,
        { withCredentials: true }
      );

      this.setState({ creditsBalance: res.data.credits });
      return res.data.credits;
    } catch (err) {
      console.error("Cannot load credits", err);
      return 0;
    }
  };

  /* ----------------------------
     PRODUCT FILTERS
  ----------------------------- */
  getSubscriptionProducts() {
    return this.state.products.filter((p) =>
      p.prices.some(price => price.recurring)
    );
  }

  getCreditProducts() {
    return this.state.products.filter((p) =>
      p.prices.some(price => !price.recurring)
    );
  }

  getProductFeatures(product) {
  // Try product.metadata.features first
  let raw = product?.metadata?.features;

  // Fallback: price-level metadata if you prefer that
  if (!raw && product?.prices?.[0]?.metadata?.features) {
    raw = product.prices[0].metadata.features;
  }

  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Error parsing features for product", product.id, e);
    return [];
  }
}


  /* ----------------------------
     CHECKOUT: SUBSCRIPTION
  ----------------------------- */
  handleSubscribe = async (lookupKey) => {
    try {
      this.setState({ busyAction: 'subscribe' });

      const res = await axios.post(
        `${this.API_BASE}/stripe/subscribe/session`,
        { lookupKey },
        { withCredentials: true }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert("Unable to start subscription checkout");
      }
    } catch (err) {
      console.error("Subscribe error", err);
      alert("Unable to start subscription.");
    } finally {
      this.setState({ busyAction: null });
    }
  };

  /* ----------------------------
     CHECKOUT: TOP-UP
  ----------------------------- */
  handleTopup = async (lookupKey) => {
    try {
      this.setState({ busyAction: 'topup' });

      const res = await axios.post(
        `${this.API_BASE}/stripe/topup/session`,
        { lookupKey },
        { withCredentials: true }
      );

      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert("Unable to start credit checkout");
      }
    } catch (err) {
      console.error("Topup error", err);
      alert("Unable to top up credits.");
    } finally {
      this.setState({ busyAction: null });
    }
  };

  /* ----------------------------
     RENDER PRICE TAG
  ----------------------------- */
  renderPriceTag(price) {
    if (!price) return null;

    const amount = (price.unit_amount || 0) / 100;
    const currency = (price.currency || 'aud').toUpperCase();

    if (price.recurring) {
      return (
        <span className="font-semibold text-slate-900">
          {currency} {amount.toFixed(2)}
          <span className="font-normal text-slate-500">
            / {price.recurring.interval}
          </span>
        </span>
      );
    }

    return (
      <span className="font-semibold text-slate-900">
        {currency} {amount.toFixed(2)}{' '}
        <span className="font-normal text-slate-500">one-off</span>
      </span>
    );
  }

  /* ----------------------------
   RENDER PRODUCT CARD
  ----------------------------- */
  renderProductCard(product, isSubscription) {
    const price = product.prices[0];
    const lookupKey = price.lookup_key;
    const features = this.getProductFeatures(product); 

    return (
      <div
        key={product.id}
        className="bg-white rounded-xl w-[400px] h-[460px] shadow-xl p-6 flex flex-col justify-between hover:scale-[1.08] hover:shadow-2xl"
      >
        <div>
          <h3 className="text-2xl text-center font-semibold mb-1 bg-sky-800 text-white p-2 rounded-xl">
            {product.name}
          </h3>

          {product.description && (
            <p className="text-md text-center text-slate-500 mb-3">
              {product.description}
            </p>
          )}

          <div className="mb-3 text-xl text-center my-10">
            {this.renderPriceTag(price)}
          </div>

          {/* ✅ FEATURES LIST */}
          {features.length > 0 && (
            <ul className="mt-8 mb-4 text-md text-slate-700 space-y-1 list-disc list-inside">
              {features.map((f, idx) => (
                <li key={idx}>{f}</li>
              ))}
            </ul>
          )}
        </div>

        {/* BUTTON */}
        <div>
          {isSubscription ? (
            <button
              disabled={
                this.state.busyAction === "subscribe" ||
                this.state.hasActiveSubscription
              }
              className={
                "w-full inline-flex items-center justify-center rounded-xl text-lg font-medium px-4 py-2 transition " +
                (this.state.hasActiveSubscription
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-sky-700 text-white hover:bg-sky-800")
              }
              onClick={() =>
                !this.state.hasActiveSubscription &&
                this.handleSubscribe(lookupKey)
              }
            >
              {this.state.hasActiveSubscription
                ? "Already Subscribed"
                : this.state.busyAction === "subscribe"
                ? "Starting subscription…"
                : "Subscribe"}
            </button>
          ) : (
            <button
              disabled={this.state.busyAction === "topup"}
              className="w-full inline-flex items-center justify-center rounded-xl bg-sky-100 text-slate-900 text-lg font-semibold px-4 py-2 hover:bg-sky-200 disabled:bg-slate-100 transition"
              onClick={() => this.handleTopup(lookupKey)}
            >
              {this.state.busyAction === "topup"
                ? "Opening checkout…"
                : "Buy credits"}
            </button>
          )}
        </div>
      </div>
    );
  }


  /* ----------------------------
     MAIN RENDER
  ----------------------------- */
  render() {
    const { loading, error, creditsBalance } = this.state;

    if (loading) {
      return <div className="text-slate-600">Loading pricing…</div>;
    }

    if (error) {
      return (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
          {error}
        </div>
      );
    }

    const subscriptionProducts = this.getSubscriptionProducts();
    const creditProducts = this.getCreditProducts();

    return (
      <div className="space-y-8">
        {/* HEADER */}
        <div>
          <h2 className="text-4xl mt-10 text-center font-bold text-slate-900 mb-1">
            Pricing
          </h2>
          <p className="text-md text-center text-slate-500">
            Choose a monthly subscription and top up credits when needed.
          </p>
        </div>

        {/* SUBSCRIPTIONS */}
        <section className="space-y-5">
          <div>
            <h3 className="text-2xl text-center font-semibold text-slate-900">
              Subscription
            </h3>
            <p className="text-sm text-center text-slate-500">
              Fixed monthly access to Caredevo.
            </p>
          </div>

          <div className="grid grid-cols-1 place-items-center">
            {subscriptionProducts.length === 0 && (
              <p className="text-sm text-slate-500 text-center">
                No subscription plans configured.
              </p>
            )}
            {subscriptionProducts.map((p) =>
              this.renderProductCard(p, true)
            )}
          </div>
        </section>

        {/* CREDIT PACKS only Visible when there's active subscription*/}
        {this.state.hasActiveSubscription && (
       <section className="space-y-3">
          <div>
            <h3 className="text-2xl text-center font-semibold text-slate-900">
              Credit packs
            </h3>
            <p className="text-center text-slate-500">
              Buy extra AI credits whenever you need more usage.
            </p>
            <p className="text-center text-slate-500">
              Current Credits: {creditsBalance}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 place-items-center sm:grid-cols-1 xs:grid-cols-1">
            {creditProducts.length === 0 ? (
              <p className="text-sm text-slate-500 text-center">
                No credit packs configured.
              </p>
            ) : (
              creditProducts.map((p) =>
                this.renderProductCard(p, false)
              )
            )}
          </div>
        </section>



        )}
 
      </div>
    );
  }
}

export default Pricing;
